import { useState } from 'react'

import { useCompliance, useComplianceSummary, type ComplianceRow } from '../api/queries'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'

const STATUS: Record<ComplianceRow['status'], { word: string; colour: string }> = {
  fail: { word: 'failed', colour: 'var(--bad)' },
  // Not amber-as-nearly-passing. This control was not evaluated at all, and the whole
  // point of the state is that it is not a pass.
  unknown: { word: 'could not be checked', colour: 'var(--warn)' },
  pass: { word: 'passed', colour: 'var(--ok)' },
}

const SEVERITY_COLOUR: Record<ComplianceRow['severity'], string> = {
  critical: 'var(--sev-critical)',
  high: 'var(--sev-high)',
  medium: 'var(--sev-medium)',
  low: 'var(--sev-low)',
}

/**
 * Compliance — the estate's posture, and what the score does not cover.
 *
 * The caveat is rendered next to the number, not below the fold and not in a tooltip. A
 * percentage is the one thing on this screen that will be copied into a slide, and a
 * score computed over the controls that could be checked is only honest while the count
 * of the ones that could not is attached to it.
 *
 * `could not be checked` is styled as its own state rather than as a near-pass. An
 * unreadable `/etc/sudoers` scored as "no passwordless sudo rules" is a clean bill of
 * health for a file nobody looked at, and the colour has to say so as clearly as the
 * word does.
 */
export function CompliancePage({ tenants = [] }: { tenants?: string[] }) {
  const [tenant] = useTenant(tenants)
  const [status, setStatus] = useState('')
  const [hostname, setHostname] = useState('')

  const summary = useComplianceSummary(tenant)
  const rows = useCompliance({
    tenant,
    ...(status ? { status } : {}),
    ...(hostname ? { hostname } : {}),
  })

  if (summary.isError) return <Failure error={summary.error} onRetry={() => summary.refetch()} />

  return (
    <section aria-labelledby="compliance-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="compliance-heading" style={{ margin: 0 }}>
          Compliance
        </h2>
        {summary.data && (
          <span className="muted">
            {summary.data.decided} control(s) checked across {summary.data.hosts.length} host(s)
          </span>
        )}
      </header>

      {summary.data && (
        <div className="panel" style={{ marginTop: 'var(--space-4)' }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-xl, var(--text-lg))',
              color:
                summary.data.score >= 90
                  ? 'var(--ok)'
                  : summary.data.score >= 70
                    ? 'var(--warn)'
                    : 'var(--bad)',
            }}
          >
            {summary.data.decided === 0 ? '—' : `${summary.data.score}%`}
          </p>
          {/* Attached to the number, deliberately. This is the one thing on the page
              that will be copied into a slide. */}
          <p style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
            of {summary.data.decided} checked control(s) passed — {summary.data.caveat}
          </p>
          {summary.data.decided === 0 && (
            <p className="muted" style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-xs)' }}>
              Nothing has been evaluated yet. Run{' '}
              <span className="mono">manage.py compliance</span> once the agents have
              reported an inventory. An unevaluated estate scores nothing, not full marks.
            </p>
          )}
        </div>
      )}

      {summary.data && summary.data.hosts.length > 0 && (
        <div style={{ marginTop: 'var(--space-4)', overflowX: 'auto' }}>
          <table>
            <caption className="visually-hidden">Compliance posture per host</caption>
            <thead>
              <tr>
                <th scope="col">Host</th>
                <th scope="col">Score</th>
                <th scope="col">Passed</th>
                <th scope="col">Failed</th>
                <th scope="col">Unchecked</th>
              </tr>
            </thead>
            <tbody>
              {summary.data.hosts.map((host) => (
                <tr key={host.hostname}>
                  <td className="mono">
                    <button
                      className="linklike"
                      onClick={() => setHostname(host.hostname ?? '')}
                    >
                      {host.hostname}
                    </button>
                  </td>
                  {/* Per host, not the estate total divided by the host count: four
                      checkable controls and forty do not average. */}
                  <td>{host.decided === 0 ? '—' : `${host.score}%`}</td>
                  <td>{host.passed}</td>
                  <td style={{ color: host.failed ? 'var(--bad)' : undefined }}>{host.failed}</td>
                  <td style={{ color: host.unchecked ? 'var(--warn)' : undefined }}>
                    {host.unchecked}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          marginTop: 'var(--space-5)',
        }}
      >
        <label htmlFor="compliance-status" className="muted">
          Status
        </label>
        <select
          id="compliance-status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">all</option>
          <option value="fail">failed</option>
          <option value="unknown">could not be checked</option>
          <option value="pass">passed</option>
        </select>
        {hostname && (
          <button onClick={() => setHostname('')}>
            Showing {hostname} — show every host
          </button>
        )}
      </div>

      {rows.isError ? (
        <Failure error={rows.error} onRetry={() => rows.refetch()} />
      ) : !rows.data ? (
        <div aria-hidden="true" style={{ marginTop: 'var(--space-4)' }}>
          {Array.from({ length: 5 }, (_, index) => (
            <div className="skeleton" key={index} />
          ))}
        </div>
      ) : rows.data.results.length === 0 ? (
        <p className="empty">No control matches.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 'var(--space-4) 0 0', padding: 0 }}>
          {rows.data.results.map((row) => (
            <li key={row.id} style={{ marginBottom: 'var(--space-2)' }}>
              <Control row={row} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Control({ row }: { row: ComplianceRow }) {
  const [open, setOpen] = useState(false)
  const state = STATUS[row.status]

  return (
    <div
      className="panel"
      style={{ borderLeft: `3px solid ${state.colour}`, padding: 'var(--space-3)' }}
    >
      <div
        style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}
      >
        <span style={{ color: state.colour, fontSize: 'var(--text-xs)' }}>{state.word}</span>
        {row.status !== 'pass' && (
          <span style={{ color: SEVERITY_COLOUR[row.severity], fontSize: 'var(--text-xs)' }}>
            {row.severity}
          </span>
        )}
        <span className="mono">
          {row.policy_id}/{row.control_id}
        </span>
        <span className="mono">{row.hostname}</span>
        {row.status !== 'pass' && (
          <button
            style={{ marginLeft: 'auto' }}
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            How to fix
          </button>
        )}
      </div>

      <p style={{ margin: 'var(--space-2) 0 0' }}>{row.control_title}</p>
      <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
        {row.detail}
      </p>

      {row.evidence.length > 0 && (
        <ul
          className="mono"
          style={{
            listStyle: 'none',
            margin: 'var(--space-2) 0 0',
            padding: 0,
            fontSize: 'var(--text-xs)',
          }}
        >
          {/* The offending entries. This is what turns "sudo policy failed" into
              something an operator can act on without going to the host. */}
          {row.evidence.map((item) => (
            <li key={`${item.key}=${item.value}`}>
              {item.key} — {item.value}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
            <strong>Requires:</strong> {row.requirement}
          </p>
          <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)' }}>
            <strong>Fix:</strong> {row.remediation}
          </p>
          {row.rationale && (
            <p className="muted" style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)' }}>
              {row.rationale}
            </p>
          )}
          {row.references.map((href) => (
            <p key={href} style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
              <a href={href} target="_blank" rel="noreferrer noopener">
                {href}
              </a>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
